import type { FC, ReactNode } from 'react'
import type { StageMode } from '@/types'

import dynamic from 'next/dynamic'
import { ArrowLeft, Check, Drama, Hand } from 'lucide-react'
import StageLoader from '@/components/landing/stage-loader'
import StageModeToggle from '@/components/landing/stage-mode-toggle'

const MaskStage = dynamic(() => import('@/components/landing/mask-stage'), {
    ssr: false,
    loading: () => <StageLoader />
})

interface HeroSectionProps {
    mode: StageMode
    onModeChange: (mode: StageMode) => void
}

const STAGE_LABELS: Record<StageMode, string> = {
    both: 'מסכות הקומדיה והטרגדיה בתלת מימד',
    comedy: 'מסכת הקומדיה בתלת מימד',
    tragedy: 'מסכת הטרגדיה בתלת מימד'
}

const PROMISES = ['בלי ניסיון קודם', 'קבוצות לפי גיל', 'בלי התחייבות להמשך']

const HeroSection: FC<HeroSectionProps> = ({ mode, onModeChange }): ReactNode => {
    return (
        <section
            id='stage'
            aria-labelledby='stage-title'
            className='relative isolate overflow-hidden pb-14 pt-24 sm:pt-28 lg:flex lg:min-h-[100svh] lg:items-center lg:pb-16'
        >
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 -z-20 bg-[radial-gradient(ellipse_75%_55%_at_50%_0%,oklch(0.3_0.11_22/0.6),transparent_72%)]' />
            <div aria-hidden='true' className='pointer-events-none absolute inset-x-0 bottom-0 -z-20 h-40 bg-linear-to-b from-transparent to-background' />
            <div aria-hidden='true' className='curtain-fabric pointer-events-none absolute inset-y-0 left-0 -z-10 hidden w-[5vw] max-w-20 opacity-70 [mask-image:linear-gradient(to_right,black,transparent)] lg:block' />
            <div aria-hidden='true' className='curtain-fabric pointer-events-none absolute inset-y-0 right-0 -z-10 hidden w-[5vw] max-w-20 opacity-70 [mask-image:linear-gradient(to_left,black,transparent)] lg:block' />

            <div className='mx-auto grid w-full max-w-7xl items-center gap-x-12 gap-y-6 px-5 sm:px-8 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]'>
                <div className='text-center lg:text-start'>
                    <p className='inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/10 px-3.5 py-1.5 text-xs font-medium text-primary sm:text-sm'>
                        <Drama aria-hidden='true' className='size-4' />
                        סטודיו למשחק לילדים ולנוער
                    </p>

                    <h1
                        id='stage-title'
                        className='mt-5 font-heading text-5xl font-bold leading-[1.05] tracking-tight text-balance sm:text-6xl xl:text-7xl'
                    >
                        <span className='block'>
                            כאן ילדים
                        </span>
                        <span className='block bg-linear-to-l from-[#f8dc97] via-[#e2b35c] to-[#b07a2e] bg-clip-text pb-2 text-transparent'>
                            עולים לבמה.
                        </span>
                    </h1>

                    <p className='mx-auto mt-4 max-w-xl text-base leading-relaxed text-muted-foreground text-pretty sm:text-lg lg:mx-0'>
                        חוגי תיאטרון ומשחק לילדים ולנוער. משחקים, מאלתרים, בונים דמויות ומגלים ביטחון שנשאר גם אחרי שהמסך יורד. הצעד הראשון הוא שיעור ניסיון.
                    </p>

                    <div className='mt-7 flex flex-wrap items-center justify-center gap-3 lg:justify-start'>
                        <a
                            href='#trial'
                            className='inline-flex min-h-13 items-center gap-2 rounded-full bg-primary px-7 text-base font-semibold text-primary-foreground shadow-[0_12px_32px_-12px_oklch(0.8_0.12_80/0.7)] transition-all duration-300 hover:-translate-y-0.5 hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background'
                        >
                            הרשמה לשיעור ניסיון
                            <ArrowLeft aria-hidden='true' className='size-5' />
                        </a>

                        <a
                            href='#classes'
                            className='inline-flex min-h-13 items-center rounded-full border border-border px-7 text-base font-semibold text-foreground transition-colors duration-300 hover:border-primary/50 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                        >
                            לחוגים
                        </a>
                    </div>

                    <ul className='mt-7 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-foreground/80 lg:justify-start'>
                        {PROMISES.map((promise) => (
                            <li key={promise} className='flex items-center gap-2'>
                                <Check aria-hidden='true' className='size-4 text-primary' />
                                {promise}
                            </li>
                        ))}
                    </ul>
                </div>

                <div className='relative'>
                    <div aria-hidden='true' className='pointer-events-none absolute inset-x-0 -top-40 bottom-[8%] -z-10 bg-[linear-gradient(to_bottom,oklch(0.96_0.05_85/0.17),transparent_78%)] blur-xl [clip-path:polygon(40%_0,60%_0,94%_100%,6%_100%)]' />
                    <div aria-hidden='true' className='pointer-events-none absolute inset-x-[10%] top-[8%] bottom-[16%] -z-10 animate-glow rounded-full bg-[radial-gradient(closest-side,oklch(0.8_0.12_80/0.2),oklch(0.46_0.16_22/0.1)_60%,transparent)] blur-2xl motion-reduce:animate-none' />
                    <div aria-hidden='true' className='pointer-events-none absolute inset-x-[18%] bottom-[8%] -z-10 h-16 rounded-[100%] bg-primary/15 blur-2xl' />

                    <div className='relative h-[52svh] max-h-[560px] min-h-[320px] sm:h-[58svh] lg:h-[min(76svh,740px)] lg:max-h-none'>
                        <MaskStage
                            mode={mode}
                            finish='gold'
                            lighting='stage'
                            spinSignal={0}
                            label={`${STAGE_LABELS[mode]}. גררו או השתמשו במקשי החיצים כדי לסובב.`}
                            className='absolute inset-0 rounded-3xl'
                        />
                    </div>

                    <div className='mt-2 flex flex-col items-center gap-2.5'>
                        <StageModeToggle mode={mode} onModeChange={onModeChange} />

                        <p className='flex items-center gap-2 text-xs text-muted-foreground'>
                            <Hand aria-hidden='true' className='size-3.5' />
                            גררו את המסכות כדי לסובב אותן
                        </p>
                    </div>
                </div>
            </div>

            <div aria-hidden='true' className='pointer-events-none absolute inset-0 z-30 overflow-hidden motion-reduce:hidden'>
                <div className='curtain-fabric absolute inset-y-0 left-0 w-1/2 origin-left animate-curtain-left' />
                <div className='curtain-fabric absolute inset-y-0 right-0 w-1/2 origin-right animate-curtain-right' />
            </div>
        </section>
    )
}

export default HeroSection
